<script setup>
import _map from "lodash/map";
import _sum from "lodash/sum";
import ActionButton from "./ActionButton.vue";
import { IndentDecrease, IndentIncrease } from "@Icons/index";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/vue/24/outline";
import Action from "./Actions/Action";
import { nextTick, onMounted, ref, watch } from "vue";
import _cloneDeep from "lodash/cloneDeep";
import { twMerge } from "tailwind-merge";
import Tooltip from "@Components/Tooltip.vue";
import { ExternalLinkIcon } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
    placeholder: {
        type: String,
        default: "",
    },
    depthEnabled: {
        type: Boolean,
        default: false,
    },
    sortableEnabled: {
        type: Boolean,
        default: false,
    },
    actions: {
        type: Array,
        default: [],
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    disableNewRows: {
        type: Boolean,
        default: false,
    },
    class: String,
});

const emit = defineEmits([
    "update:modelValue",
    "enterKeyPressed",
    "tabKeyPressed",
    "shiftedTabKeyPressed",
    "deleteKeyPressed",
    "pastePressed",
    "fallbackKeyPressed",
    "upKeyPressed",
    "downKeyPressed",
    "removeRow",
    "moveUpPressed",
    "moveDownPressed",
    "focus",
    "blur",
]);

const { t: __ } = useI18n();

const input = ref();

const inputHandler = (e) => {
    emit("update:modelValue", {
        ...props.modelValue,
        title: e.target.value,
    });
};

const runAction = (action) => {
    action.run({ getRow, getIndex, updateRow, deleteRow });
};

const _actions = props.actions.map((options) => {
    return new Action(options);
});

const depthActions = [
    new Action({
        icon: IndentDecrease,
        label: null,
        onHandle: () => {
            emit("shiftedTabKeyPressed");
        },
    }),
    new Action({
        icon: IndentIncrease,
        label: null,
        onHandle: () => {
            emit("tabKeyPressed");
        },
    }),
];

const sortableActions = [
    new Action({
        icon: ChevronUpIcon,
        label: null,
        onHandle: () => {
            emit("moveUpPressed");
        },
    }),
    new Action({
        icon: ChevronDownIcon,
        label: null,
        onHandle: () => {
            emit("moveDownPressed");
        },
    }),
];

const updateRow = (row) => {
    emit("update:modelValue", row);
};

const deleteRow = () => {
    emit("removeRow");
};

const getRow = () => {
    return _cloneDeep(props.modelValue);
};

const getIndex = () => {
    return props.index;
};
</script>

<template>
    <div class="flex items-center space-x-3">
        <div v-if="depthEnabled || sortableEnabled" class="flex flex-none">
            <ActionButton v-if="depthEnabled" v-for="action in depthActions" :action="action" @click="runAction(action)" />
            <ActionButton v-if="sortableEnabled" v-for="action in sortableActions" :action="action" @click="runAction(action)" />
        </div>
        <div class="flex-grow relative">
            <div :class="props.class">
                <input
                    :id="'row-' + index"
                    ref="input"
                    value-input
                    :readonly="readonly"
                    :value="modelValue.title"
                    @input="inputHandler"
                    @focus="emit('focus')"
                    @blur="emit('blur')"
                    @keydown.enter.prevent="$emit('enterKeyPressed', index)"
                    @keydown.exact.tab.prevent="$emit('tabKeyPressed')"
                    @keydown.shift.tab.prevent="$emit('shiftedTabKeyPressed')"
                    @keydown.delete="$emit('deleteKeyPressed', $event)"
                    @paste.prevent="$emit('pastePressed', $event)"
                    @keydown.exact="$emit('fallbackKeyPressed')"
                    @keydown.up.prevent="$emit('upKeyPressed')"
                    @keydown.down.prevent="$emit('downKeyPressed')"
                    type="text"
                    :style="'padding-left:' + (0.75 + 1.75 * modelValue.depth) + 'rem;'"
                    :class="[
                        modelValue.meta.url ? 'pr-8' : 'pr-3',
                        'w-full block py-1.5 text-sm leading-6 text-gray-900 bg-white outline-none border-0 ring-0 dark:text-white dark:bg-gray-900 dark:placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400 ',
                    ]"
                    :placeholder="placeholder" />
                <div v-if="modelValue.meta.url" class="absolute inset-y-0 right-0 flex items-center gap-x-1 mr-2 select-none">
                    <Tooltip :content="__('Voir la source')" placement="top">
                        <a :href="modelValue.meta.url" target="_blank">
                            <ExternalLinkIcon class="w-4 h-4 text-gray-600 cursor-pointer dark:text-gray-400 dark:hover:text-white hover:text-gray-900" />
                        </a>
                    </Tooltip>
                </div>
            </div>
        </div>
        <div class="flex flex-none">
            <ActionButton
                v-for="action in _actions"
                :action="action"
                :params="{ getRow, getIndex, updateRow, deleteRow }"
                @click="runAction(action, undefined)" />
        </div>
    </div>
</template>
